import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, userAPI } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check if user is logged in on mount
    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await authAPI.getMe();
            setUser(response.data || response.user);
        } catch (err) {
            localStorage.removeItem('token');
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const register = async (name, email, password) => {
        setError(null);
        try {
            const response = await authAPI.register({ name, email, password });
            localStorage.setItem('token', response.token);
            setUser(response.data || response.user);
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const login = async (email, password) => {
        setError(null);
        try {
            const response = await authAPI.login({ email, password });
            localStorage.setItem('token', response.token);
            setUser(response.data || response.user);
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (err) {
            // Ignore error, still logout locally
        } finally {
            localStorage.removeItem('token');
            setUser(null);
        }
    };

    const updateUser = (userData) => {
        setUser(prev => ({ ...prev, ...userData }));
    };

    const updateProfile = async (profileData) => {
        setError(null);
        try {
            const response = await userAPI.updateProfile(profileData);
            if (response.user) {
                setUser(prev => ({ ...prev, ...response.user }));
            }
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const updateSettings = async (settings) => {
        setError(null);
        try {
            const response = await userAPI.updateSettings(settings);
            if (response.settings) {
                setUser(prev => ({
                    ...prev,
                    settings: {
                        ...(prev?.settings || {}),
                        ...response.settings
                    }
                }));
            }
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const changePassword = async (currentPassword, newPassword) => {
        setError(null);
        try {
            return await userAPI.changePassword({ currentPassword, newPassword });
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const forgotPassword = async (email) => {
        setError(null);
        try {
            return await authAPI.forgotPassword(email);
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const resetPassword = async (token, password) => {
        setError(null);
        try {
            const response = await authAPI.resetPassword(token, password);
            if (response.token) {
                localStorage.setItem('token', response.token);
                setUser(response.data || response.user);
            }
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const verifyEmail = async (token) => {
        setError(null);
        try {
            const response = await authAPI.verifyEmail(token);
            if (localStorage.getItem('token')) {
                await checkAuth();
            }
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const exportUserData = async () => {
        setError(null);
        try {
            return await userAPI.exportData();
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const deleteAccount = async (password) => {
        setError(null);
        try {
            const response = await userAPI.deleteAccount(password);
            localStorage.removeItem('token');
            setUser(null);
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Check if user has premium access
    const isPremium = () => {
        if (!user) return false;
        const { subscription } = user;
        if (!subscription) return false;
        if (subscription.status === 'premium' && subscription.active) return true;
        if (subscription.status === 'trial' && subscription.active) return true;
        return false;
    };

    // Get remaining trial days
    const getTrialDays = () => {
        if (!user) return 0;
        return user.subscription?.remainingDays || 0;
    };

    // Check if user is admin
    const isAdmin = () => {
        if (!user) return false;
        return user.isAdmin || user.role === 'admin' || user.role === 'superadmin';
    };

    // Check if user is superadmin
    const isSuperAdmin = () => {
        if (!user) return false;
        return user.role === 'superadmin';
    };

    const value = {
        user,
        loading,
        error,
        isAuthenticated: !!user,
        isPremium: isPremium(),
        isAdmin: isAdmin(),
        isSuperAdmin: isSuperAdmin(),
        trialDays: getTrialDays(),
        register,
        login,
        logout,
        forgotPassword,
        resetPassword,
        verifyEmail,
        updateProfile,
        updateSettings,
        changePassword,
        exportUserData,
        deleteAccount,
        updateUser,
        checkAuth,
        setError,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
